import { ref } from 'vue'
import { defineStore } from 'pinia'

export const themes = [
  // ── Light themes ──────────────────────────────────────────────────────────
  {
    id: 'slate',
    label: 'Slate',
    description: 'Classic dark blue-gray',
    preview: ['#0f172a', '#2563eb', '#f8fafc'],
    dark: false,
  },
  {
    id: 'ocean',
    label: 'Ocean',
    description: 'Deep navy & cyan',
    preview: ['#0c1a2e', '#06b6d4', '#f0f9ff'],
    dark: false,
  },
  {
    id: 'forest',
    label: 'Forest',
    description: 'Dark green & emerald',
    preview: ['#0d2818', '#10b981', '#f0fdf4'],
    dark: false,
  },
  {
    id: 'rose',
    label: 'Rose',
    description: 'Deep purple & violet',
    preview: ['#1a0a2e', '#8b5cf6', '#faf5ff'],
    dark: false,
  },
  {
    id: 'amber',
    label: 'Amber',
    description: 'Warm dark & amber',
    preview: ['#1c1008', '#f59e0b', '#fffbeb'],
    dark: false,
  },
  // ── Dark themes ───────────────────────────────────────────────────────────
  {
    id: 'carbon',
    label: 'Carbon',
    description: 'Soot black · steel-blue',
    preview: ['#16161c', '#60a5fa', '#2c2c38'],
    dark: true,
  },
  {
    id: 'midnight',
    label: 'Midnight',
    description: 'Space black · indigo glow',
    preview: ['#101026', '#818cf8', '#1e1e3e'],
    dark: true,
  },
  {
    id: 'graphite',
    label: 'Graphite',
    description: 'Charcoal · neon cyan',
    preview: ['#12121a', '#22d3ee', '#20202e'],
    dark: true,
  },
  {
    id: 'obsidian',
    label: 'Obsidian',
    description: 'Volcanic · rose ember',
    preview: ['#180a0f', '#fb7185', '#2a1219'],
    dark: true,
  },
  {
    id: 'onyx',
    label: 'Onyx',
    description: 'Pure void · acid green',
    preview: ['#0e1410', '#4ade80', '#1a2a1e'],
    dark: true,
  },
] as const

export type ThemeId = (typeof themes)[number]['id']

export const useThemeStore = defineStore('theme', () => {
  const current = ref<ThemeId>(
    (localStorage.getItem('app-theme') as ThemeId) ?? 'slate',
  )
  const syncing = ref(false)

  function _apply(id: ThemeId) {
    current.value = id
    localStorage.setItem('app-theme', id)
    document.documentElement.setAttribute('data-theme', id)
    const isDark = themes.find((t) => t.id === id)?.dark ?? false
    document.documentElement.toggleAttribute('data-dark', isDark)
  }

  /** Apply localStorage theme immediately (call before auth session restore). */
  function init() {
    document.documentElement.setAttribute('data-theme', current.value)
    const isDark = themes.find((t) => t.id === current.value)?.dark ?? false
    document.documentElement.toggleAttribute('data-dark', isDark)
  }

  /**
   * Set theme optimistically (instant UI update) then persist to backend.
   * Falls back silently if the preferences endpoint is not yet deployed.
   */
  async function setTheme(id: ThemeId) {
    _apply(id)
    try {
      const { userPreferencesApi } = await import('@/api')
      await userPreferencesApi.update({ theme: id })
    } catch {
      // Silently ignore — the preference is already saved in localStorage
    }
  }

  /**
   * Load the user's saved theme from the backend after a session is restored.
   * Falls back to the localStorage value if the endpoint isn't available.
   */
  async function syncFromServer() {
    syncing.value = true
    try {
      const { userPreferencesApi } = await import('@/api')
      const { data } = await userPreferencesApi.get()
      const serverTheme = data?.data?.theme as ThemeId | undefined
      if (serverTheme && themes.some((t) => t.id === serverTheme)) {
        _apply(serverTheme)
      }
    } catch {
      // Endpoint not deployed or user has no preference yet — keep localStorage value
    } finally {
      syncing.value = false
    }
  }

  return { current, syncing, setTheme, init, syncFromServer }
})
